import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  Cell,
  CartesianGrid,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Download, ExternalLink, FileUp, Flag, MapPin, Pencil, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/useAuth';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { apiRequest, downloadApiFile, getApiErrorMessage } from '../../lib/api';
import type {
  PaginatedResponse,
  SurveyDashboardResponse,
  SurveyChartColors,
  SurveyOutletReview,
  SurveyReportChartType,
  SurveyReportingOptions,
  SurveyVisit,
  SurveyVisitDetail,
} from '../../types/domain';
import { ManualSurveyDialog } from './ManualSurveyDialog';
import { ManualSurveyEditForm } from './ManualSurveyEditForm';

type SurveyTab = 'dashboard' | 'visits' | 'outlets';

const dateTime = new Intl.DateTimeFormat('en-PK', {
  day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
  timeZone: 'Asia/Karachi',
});

const chartTooltipStyle = {
  backgroundColor: 'var(--paper)',
  border: '1px solid var(--line)',
  borderRadius: 10,
  boxShadow: 'var(--shadow)',
  color: 'var(--ink)',
  fontSize: 12,
};

const availabilityPieColors = ['#2f8065', '#d9a72f', '#4f7fa8', '#c36b94', '#8a7440', '#658b70', '#8a718b'];

const defaultSurveyChartColors: SurveyChartColors = {
  coverage_by_city: '#2F8065',
  question_default: '#4F7FA8',
  availability_by_city: {
    premium_comfort_presence: '#2F8065',
    savers_presence: '#D9A72F',
    known_competitor_category_presence: '#78847D',
  },
  availability_sections: {},
  question_options: {
    available: '#2F8065',
    not_available: '#78847D',
    answered: '#4F7FA8',
  },
};

function resolvedChartColors(colors: SurveyChartColors | undefined): SurveyChartColors {
  return {
    ...defaultSurveyChartColors,
    ...colors,
    availability_by_city: {
      ...defaultSurveyChartColors.availability_by_city,
      ...colors?.availability_by_city,
    },
    availability_sections: {
      ...defaultSurveyChartColors.availability_sections,
      ...colors?.availability_sections,
    },
    question_options: {
      ...defaultSurveyChartColors.question_options,
      ...colors?.question_options,
    },
  };
}

function availabilityPieColor(index: number) {
  return availabilityPieColors[index % availabilityPieColors.length] ?? '#2f8065';
}

function SurveyDistributionChart({
  data,
  type,
  color,
  colors = {},
  showLegend = true,
}: {
  data: Array<{ key?: string; value?: string; label: string; count: number }>;
  type: SurveyReportChartType;
  color: string;
  colors?: Record<string, string>;
  showLegend?: boolean;
}) {
  const itemColor = (item: { key?: string; value?: string }, index: number) => (
    colors[item.value ?? item.key ?? ''] ?? (type === 'bar' ? color : availabilityPieColor(index))
  );
  if (type === 'bar') {
    return <ResponsiveContainer height="100%" width="100%"><BarChart accessibilityLayer data={data} margin={{ top: 14, right: 12, left: -20, bottom: 4 }}><CartesianGrid stroke="var(--line)" strokeDasharray="3 5" vertical={false} /><XAxis dataKey="label" tick={{ fill: 'var(--muted)', fontSize: 12 }} /><YAxis allowDecimals={false} tick={{ fill: 'var(--muted)', fontSize: 12 }} /><Tooltip contentStyle={chartTooltipStyle} cursor={{ fill: 'var(--surface-soft)' }} /><Bar dataKey="count" fill={color} name="Responses" radius={[5, 5, 0, 0]}>{data.map((item, index) => <Cell fill={itemColor(item, index)} key={item.value ?? item.key ?? item.label} />)}</Bar></BarChart></ResponsiveContainer>;
  }
  return <ResponsiveContainer height="100%" width="100%"><PieChart accessibilityLayer><Pie data={data} dataKey="count" nameKey="label" cx="50%" cy="50%" innerRadius={type === 'donut' ? 45 : 0} outerRadius={95} paddingAngle={2}>{data.map((item, index) => <Cell fill={itemColor(item, index)} key={item.value ?? item.key ?? item.label} />)}</Pie><Tooltip contentStyle={chartTooltipStyle} />{showLegend ? <Legend wrapperStyle={{ fontSize: 12 }} /> : null}</PieChart></ResponsiveContainer>;
}

function displayAnswer(value: unknown): string {
  if (value === true) return 'Available';
  if (value === false) return 'Not available';
  if (Array.isArray(value)) return value.join(', ');
  if (value && typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return 'Not answered';
}

function percentage(value: number | null) {
  return value === null ? 'Not available' : `${value.toFixed(1)}%`;
}

function surveyGoogleMapsUrl(latitude: string, longitude: string) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${latitude},${longitude}`)}`;
}

function SurveyMapLink({ latitude, longitude, name }: { latitude: string; longitude: string; name: string }) {
  return <a
    aria-label={`Open ${name} survey coordinates in Google Maps (opens in a new tab)`}
    className="store-map-link"
    href={surveyGoogleMapsUrl(latitude, longitude)}
    rel="noopener noreferrer"
    target="_blank"
    title="Open coordinates in Google Maps"
  ><MapPin size={15} /></a>;
}

function DashboardMetric({ label, value, note }: { label: string; value: string | number; note?: string }) {
  return <article className="survey-dashboard-metric"><strong>{value}</strong><span>{label}</span>{note ? <small>{note}</small> : null}</article>;
}

export function SurveysPage() {
  const { accessToken, capabilities, selectedWorkspace } = useAuth();
  const [activeTab, setActiveTab] = useState<SurveyTab>('visits');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [campaign, setCampaign] = useState('');
  const [templateVersion, setTemplateVersion] = useState('');
  const [brandAmbassador, setBrandAmbassador] = useState('');
  const [city, setCity] = useState('');
  const [area, setArea] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [integrityFlag, setIntegrityFlag] = useState('');
  const [selectedVisitId, setSelectedVisitId] = useState<string | null>(null);
  const [editingManualVisit, setEditingManualVisit] = useState(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const workspaceId = selectedWorkspace?.organizationId ?? '';

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [search]);

  const reportParams = useMemo(() => {
    const params = new URLSearchParams();
    if (workspaceId) params.set('workspace', workspaceId);
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (campaign) params.set('campaign', campaign);
    if (templateVersion) params.set('template_version', templateVersion);
    if (brandAmbassador) params.set('brand_ambassador', brandAmbassador);
    if (city.trim()) params.set('city', city.trim());
    if (area.trim()) params.set('area', area.trim());
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    if (integrityFlag) params.set('integrity_flag', integrityFlag);
    return params;
  }, [area, brandAmbassador, campaign, city, dateFrom, dateTo, debouncedSearch, integrityFlag, templateVersion, workspaceId]);

  const reviewPath = useMemo(() => {
    const params = new URLSearchParams(reportParams);
    params.set('page', String(page));
    params.set('page_size', '25');
    params.set('ordering', '-business_date');
    return `/surveys/review/?${params.toString()}`;
  }, [page, reportParams]);
  const dashboardPath = useMemo(() => `/surveys/dashboard/?${reportParams.toString()}`, [reportParams]);
  const compatibilityExportPath = useMemo(
    () => `/surveys/dashboard/export/?${reportParams.toString()}`,
    [reportParams],
  );

  const reportingOptions = useQuery({
    queryKey: ['survey-reporting-options', workspaceId, accessToken],
    enabled: Boolean(accessToken),
    queryFn: () => apiRequest<SurveyReportingOptions>(
      `/surveys/reporting-options/?${workspaceId ? new URLSearchParams({ workspace: workspaceId }).toString() : ''}`,
      accessToken ? { accessToken } : {},
    ),
  });
  const dashboard = useQuery({
    queryKey: ['survey-dashboard', dashboardPath, accessToken],
    enabled: Boolean(accessToken) && activeTab === 'dashboard',
    queryFn: () => apiRequest<SurveyDashboardResponse>(dashboardPath, accessToken ? { accessToken } : {}),
  });
  const visits = useQuery({
    queryKey: ['survey-visits', reviewPath, accessToken],
    enabled: Boolean(accessToken) && activeTab === 'visits',
    queryFn: () => apiRequest<PaginatedResponse<SurveyVisit>>(
      reviewPath, accessToken ? { accessToken } : {},
    ),
  });
  const outletPath = useMemo(() => {
    const params = new URLSearchParams({ page: String(page), page_size: '25' });
    if (workspaceId) params.set('workspace', workspaceId);
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (city.trim()) params.set('city', city.trim());
    if (area.trim()) params.set('area', area.trim());
    return `/surveys/outlet-review/?${params.toString()}`;
  }, [area, city, debouncedSearch, page, workspaceId]);
  const outlets = useQuery({
    queryKey: ['survey-outlet-review', outletPath, accessToken],
    enabled: Boolean(accessToken) && activeTab === 'outlets',
    queryFn: () => apiRequest<PaginatedResponse<SurveyOutletReview>>(
      outletPath, accessToken ? { accessToken } : {},
    ),
  });
  const detail = useQuery({
    queryKey: ['survey-visit-detail', selectedVisitId, workspaceId, accessToken],
    enabled: Boolean(accessToken && selectedVisitId),
    queryFn: () => {
      const params = new URLSearchParams();
      if (workspaceId) params.set('workspace', workspaceId);
      return apiRequest<SurveyVisitDetail>(
        `/surveys/review/${selectedVisitId}/?${params.toString()}`,
        accessToken ? { accessToken } : {},
      );
    },
  });

  const cityExportPath = useMemo(() => `/surveys/review/export/?${reportParams.toString()}`, [reportParams]);
  const cityExportMutation = useMutation({
    mutationFn: () => downloadApiFile(cityExportPath, accessToken ? { accessToken } : {}),
  });
  const compatibilityExportMutation = useMutation({
    mutationFn: () => downloadApiFile(compatibilityExportPath, accessToken ? { accessToken } : {}),
  });
  const visitRows = visits.data?.results ?? [];
  const outletRows = outlets.data?.results ?? [];
  const modalVisit = detail.data;
  const modalPhoto = modalVisit?.photos[0]?.photo ?? modalVisit?.storefront_photo;
  const questionSummaries = dashboard.data?.question_summaries ?? [];
  const selectedQuestion = questionSummaries.find((question) => question.id === selectedQuestionId) ?? questionSummaries[0];

  function updateFilter(setter: (value: string) => void, value: string) {
    setter(value);
    setPage(1);
  }

  const currentCount = activeTab === 'dashboard'
    ? dashboard.data?.summary.visits ?? 0
    : activeTab === 'visits' ? visits.data?.count ?? 0 : outlets.data?.count ?? 0;
  const currentCountLabel = activeTab === 'outlets' ? 'shops' : 'visits';
  const options = reportingOptions.data;

  return <>
    <header className="page-heading">
      <div><p className="eyebrow">{selectedWorkspace?.name}</p><h1>Field surveys</h1><p>Understand authorised shop visits, availability and coverage, then export the familiar XLSX workbook alongside the web dashboard.</p></div>
      <div className="page-heading-actions">{capabilities.includes('surveys:manual_entry') ? <button className="button primary" onClick={() => setShowManualEntry(true)} type="button"><FileUp size={16} /> Upload paper surveys</button> : null}<span className="record-count"><MapPin size={16} /> {currentCount} {currentCountLabel}</span></div>
    </header>

    <nav aria-label="Survey reporting sections" className="campaign-setup-tabs survey-tabs">
      <button className={activeTab === 'dashboard' ? 'active' : ''} onClick={() => { setActiveTab('dashboard'); setPage(1); }} type="button">Dashboard</button>
      <button className={activeTab === 'visits' ? 'active' : ''} onClick={() => { setActiveTab('visits'); setPage(1); }} type="button">Submitted visits</button>
      <button className={activeTab === 'outlets' ? 'active' : ''} onClick={() => { setActiveTab('outlets'); setPage(1); }} type="button">Discovered shops</button>
    </nav>

    <section className="content-card management-form survey-reporting-filters">
      <label>Search<input aria-label="Search survey reporting" onChange={(event) => updateFilter(setSearch, event.target.value)} placeholder={activeTab === 'outlets' ? 'Shop, city, area or address' : 'Shop, city, BA or campaign'} value={search} /></label>
      {activeTab !== 'outlets' ? <><label>Campaign<select aria-label="Survey campaign" onChange={(event) => updateFilter(setCampaign, event.target.value)} value={campaign}><option value="">All campaigns</option>{(options?.campaigns ?? []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
      <label>Survey form<select aria-label="Survey form" onChange={(event) => updateFilter(setTemplateVersion, event.target.value)} value={templateVersion}><option value="">All forms</option>{(options?.template_versions ?? []).map((item) => <option key={item.id} value={item.id}>{item.name} · v{item.version}</option>)}</select></label>
      <label>Brand ambassador<select aria-label="Survey brand ambassador" onChange={(event) => updateFilter(setBrandAmbassador, event.target.value)} value={brandAmbassador}><option value="">All BAs</option>{(options?.brand_ambassadors ?? []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label></> : null}
      <label>City<input aria-label="Survey city" list="survey-city-options" onChange={(event) => updateFilter(setCity, event.target.value)} placeholder="e.g. Lahore" value={city} /><datalist id="survey-city-options">{(options?.cities ?? []).map((option) => <option key={option} value={option} />)}</datalist></label>
      <label>Area<input aria-label="Survey area" onChange={(event) => updateFilter(setArea, event.target.value)} placeholder="Optional" value={area} /></label>
      {activeTab !== 'outlets' ? <><label>From<input aria-label="Survey date from" onChange={(event) => updateFilter(setDateFrom, event.target.value)} type="date" value={dateFrom} /></label>
      <label>To<input aria-label="Survey date to" onChange={(event) => updateFilter(setDateTo, event.target.value)} type="date" value={dateTo} /></label>
      <label>Evidence signal<select aria-label="Survey evidence signal" onChange={(event) => updateFilter(setIntegrityFlag, event.target.value)} value={integrityFlag}><option value="">All visits</option><option value="mock_location">Mock location</option><option value="stale_location">Stale location</option><option value="inaccurate_location">Low GPS accuracy</option><option value="location_sample_drift">GPS sample drift</option><option value="location_capture_time_mismatch">Location time mismatch</option></select></label></> : null}
    </section>

    {activeTab === 'dashboard' ? <SurveyDashboard
      data={dashboard.data}
      error={dashboard.isError}
      loading={dashboard.isLoading}
      exportError={compatibilityExportMutation.isError ? getApiErrorMessage(compatibilityExportMutation.error) : ''}
      exporting={compatibilityExportMutation.isPending}
      onExport={() => compatibilityExportMutation.mutate()}
      onQuestionChange={setSelectedQuestionId}
      selectedQuestion={selectedQuestion}
    /> : null}

    {activeTab === 'visits' ? <>
      <div className="section-heading"><div><h2>Submitted shop visits</h2><p>Owner details remain in the restricted visit detail and are excluded from exports.</p></div><button className="button secondary" disabled={!city.trim() || cityExportMutation.isPending} onClick={() => cityExportMutation.mutate()} type="button"><Download size={16} /> {cityExportMutation.isPending ? 'Preparing XLSX…' : 'Export city XLSX'}</button></div>
      {cityExportMutation.isError ? <p className="form-error">{getApiErrorMessage(cityExportMutation.error)}</p> : null}
      {!city.trim() ? <p className="inline-help">Select a city to enable its raw XLSX export.</p> : null}
      <DataPanel error={visits.isError} loading={visits.isLoading} empty={visitRows.length === 0}>
        <section className="content-card data-card"><div className="data-table-wrap"><table className="data-table survey-visits-table"><thead><tr><th>Shop</th><th>Campaign</th><th>Brand ambassador</th><th>Visit</th><th>Coordinates</th><th>Evidence</th><th aria-label="Open visit" /></tr></thead><tbody>{visitRows.map((visit) => <tr key={visit.id}>
          <td><strong>{visit.outlet_name_snapshot}</strong><small><MapPin size={12} /> {visit.outlet_area_snapshot ? `${visit.outlet_area_snapshot} · ` : ''}{visit.outlet_city_snapshot}</small><small>{visit.outlet_source === 'catalogue' ? 'Catalogue store' : visit.outlet_source === 'activity_session' ? 'Activity session' : 'Discovered shop'} · {visit.collection_source === 'manual' ? 'Manually entered' : 'Field app'}</small></td>
          <td><strong>{visit.campaign_name}</strong><small>{visit.template_name} · v{visit.template_version}</small></td>
          <td>{visit.brand_ambassador_name}</td>
          <td><strong>{new Intl.DateTimeFormat('en-PK', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(`${visit.business_date}T00:00:00`))}</strong><small>{dateTime.format(new Date(visit.captured_at))} · {visit.reference}</small></td>
          <td className="muted-cell">{visit.latitude !== null && visit.longitude !== null ? <div className="store-location-with-map"><SurveyMapLink latitude={visit.latitude} longitude={visit.longitude} name={visit.outlet_name_snapshot} /><span>{visit.latitude}, {visit.longitude}</span></div> : 'Not provided'}</td>
          <td>{visit.collection_source === 'manual' ? <StatusPill value="manual" /> : visit.integrity_flags.length ? <span className="survey-flags"><Flag size={13} /> {visit.integrity_flags.join(', ').replaceAll('_', ' ')}</span> : <StatusPill value="submitted" />}</td>
          <td><button aria-label={`Open ${visit.outlet_name_snapshot} visit`} className="button secondary small" onClick={() => { setEditingManualVisit(false); setSelectedVisitId(visit.id); }} type="button"><ExternalLink size={14} /> Open</button></td>
        </tr>)}</tbody></table></div><Pagination page={page} previous={visits.data?.previous} next={visits.data?.next} onChange={setPage} /></section>
      </DataPanel>
    </> : null}

    {activeTab === 'outlets' ? <>
      <div className="section-heading"><div><h2>Discovered shops</h2><p>Use this lookup to investigate possible duplicates before a later, explicit resolution workflow. Records are never merged automatically.</p></div><Search size={20} aria-hidden="true" /></div>
      <DataPanel error={outlets.isError} loading={outlets.isLoading} empty={outletRows.length === 0}>
        <section className="content-card data-card"><div className="data-table-wrap"><table className="data-table"><thead><tr><th>Shop</th><th>City and area</th><th>Submitted visits</th><th>Latest visit</th></tr></thead><tbody>{outletRows.map((outlet) => <tr key={outlet.id}><td><strong>{outlet.name}</strong><small>{outlet.address || 'No address provided'}</small></td><td>{outlet.area ? `${outlet.area} · ` : ''}{outlet.city}</td><td>{outlet.visit_count}</td><td>{outlet.latest_visit_at ? dateTime.format(new Date(outlet.latest_visit_at)) : 'No submitted visit'}</td></tr>)}</tbody></table></div><Pagination page={page} previous={outlets.data?.previous} next={outlets.data?.next} onChange={setPage} /></section>
      </DataPanel>
    </> : null}

    {selectedVisitId ? (
      <div className="confirmation-backdrop" role="presentation">
        <section aria-label="Survey visit detail" aria-modal="true" className="confirmation-dialog survey-visit-detail" role="dialog">
          {detail.isLoading ? <p>Loading visit…</p> : detail.isError ? <p className="form-error">{getApiErrorMessage(detail.error)}</p> : modalVisit ? (
            editingManualVisit && accessToken ? <>
              <p className="eyebrow">{modalVisit.reference}</p>
              <h2>Edit {modalVisit.outlet_name_snapshot}</h2>
              <ManualSurveyEditForm accessToken={accessToken} onCancel={() => setEditingManualVisit(false)} onSaved={() => { setEditingManualVisit(false); void detail.refetch(); void visits.refetch(); void dashboard.refetch(); }} visit={modalVisit} workspaceId={workspaceId} />
            </> : <>
              <div className={`survey-visit-evidence${modalPhoto ? ' has-photo' : ''}`}>
                <header className="survey-visit-heading">
                  <p className="eyebrow">{modalVisit.reference}</p>
                  <h2>{modalVisit.outlet_name_snapshot}</h2>
                  <p>{modalVisit.outlet_address_snapshot || `${modalVisit.outlet_area_snapshot}${modalVisit.outlet_area_snapshot ? ' · ' : ''}${modalVisit.outlet_city_snapshot}`}</p>
                </header>
                {modalPhoto ? <img alt={`Storefront at ${modalVisit.outlet_name_snapshot}`} className="survey-storefront-photo" src={modalPhoto} /> : null}
                <div className="survey-visit-meta">
                  <span><strong>Visit</strong>{dateTime.format(new Date(modalVisit.captured_at))}</span>
                  <span><strong>Brand ambassador</strong>{modalVisit.brand_ambassador_name}</span>
                  <span><strong>Collection</strong>{modalVisit.collection_source === 'manual' ? `Manually entered by ${modalVisit.manually_entered_by_name ?? 'administrator'}` : 'Field app'}</span>
                  <span><strong>{modalVisit.collection_source === 'manual' ? 'Paper GPS details' : 'GPS evidence'}</strong>{modalVisit.horizontal_accuracy_m === null ? 'Not provided on paper form' : `±${Number(modalVisit.horizontal_accuracy_m).toFixed(1)} m${modalVisit.collection_source === 'manual' ? ' · manually supplied' : ` · ${modalVisit.location_mocked ? 'Mocked signal reported' : 'No mocked signal'}`}`}</span>
                  <span className="survey-contact-card"><strong>Shop contact details</strong><span>{modalVisit.outlet_owner_name || 'No owner name recorded'}</span>{modalVisit.outlet_owner_contact ? <span>{modalVisit.outlet_owner_contact}</span> : null}</span>
                </div>
              </div>
              {modalVisit.collection_source === 'manual' || modalVisit.integrity_flags.length ? (
                <div className="survey-visit-signals">
                  {modalVisit.collection_source === 'manual' ? <section className="survey-integrity"><h3>Manual entry reason</h3><p>{modalVisit.manual_entry_reason}</p></section> : null}
                  {modalVisit.integrity_flags.length ? <section className="survey-integrity"><h3><Flag size={16} /> Integrity signals</h3><p>{modalVisit.integrity_flags.join(', ').replaceAll('_', ' ')}</p></section> : null}
                </div>
              ) : null}
              <section className="survey-answer-list"><h3>Submitted answers</h3>{modalVisit.answers.map((answer) => <div key={answer.question_id}><strong>{answer.question_key_snapshot.replaceAll('_', ' ')}</strong><span>{displayAnswer(answer.value)}</span></div>)}</section>
              <div className="confirmation-actions">
                <button className="button secondary" onClick={() => setSelectedVisitId(null)} type="button">Close</button>
                {modalVisit.collection_source === 'manual' && capabilities.includes('surveys:manual_entry') ? <button className="button primary" onClick={() => setEditingManualVisit(true)} type="button"><Pencil size={16} /> Edit manual evidence</button> : null}
              </div>
            </>
          ) : null}
          {detail.isLoading || detail.isError || !modalVisit ? <div className="confirmation-actions"><button className="button secondary" onClick={() => { setEditingManualVisit(false); setSelectedVisitId(null); }} type="button">Close</button></div> : null}
        </section>
      </div>
    ) : null}
    {showManualEntry && accessToken ? <ManualSurveyDialog accessToken={accessToken} onClose={() => setShowManualEntry(false)} onSaved={() => { setShowManualEntry(false); void visits.refetch(); void dashboard.refetch(); void reportingOptions.refetch(); }} workspaceId={workspaceId} /> : null}
  </>;
}

function SurveyDashboard({
  data, error, loading, exporting, exportError, onExport, onQuestionChange, selectedQuestion,
}: {
  data: SurveyDashboardResponse | undefined;
  error: boolean;
  loading: boolean;
  exporting: boolean;
  exportError: string;
  onExport: () => void;
  onQuestionChange: (questionId: string) => void;
  selectedQuestion: SurveyDashboardResponse['question_summaries'][number] | undefined;
}) {
  const [availabilityCity, setAvailabilityCity] = useState('');
  const summary = data?.summary;
  const availabilityCities = useMemo(
    () => data?.availability_chart.cities ?? [],
    [data?.availability_chart.cities],
  );
  const selectedAvailabilityCity = availabilityCities.some((item) => item.city === availabilityCity)
    ? availabilityCity
    : '';
  const availabilitySections = selectedAvailabilityCity
    ? availabilityCities.find((item) => item.city === selectedAvailabilityCity)?.sections ?? []
    : data?.availability_chart.sections ?? [];
  const availabilityTotal = availabilitySections.reduce((total, section) => total + section.count, 0);
  const reportingGroups = data?.reporting_groups ?? [];
  const availabilityChartType = data?.chart_configuration.availability_mix ?? 'donut';
  const questionChartType = data?.chart_configuration.question_distribution ?? 'bar';
  const chartColors = resolvedChartColors(data?.chart_configuration.colors);
  const availabilitySectionColor = (key: string, index: number) => (
    chartColors.availability_sections[key] ?? availabilityPieColor(index)
  );

  return <>
    <div className="section-heading"><div><h2>Survey dashboard</h2><p>Server-calculated coverage and availability. The workbook keeps the same filters and city-level source data.</p></div><button className="button secondary" disabled={!summary?.visits || exporting} onClick={onExport} type="button"><Download size={16} /> {exporting ? 'Preparing XLSX…' : 'Export compatibility XLSX'}</button></div>
    {exportError ? <p className="form-error">{exportError}</p> : null}
    <DataPanel error={error} loading={loading} empty={(summary?.visits ?? 0) === 0}>
      {summary && data ? <>
        <section className="survey-dashboard-metrics" aria-label="Survey coverage summary">
          <DashboardMetric label="Survey visits" value={summary.visits.toLocaleString('en-PK')} />
          <DashboardMetric label="Distinct shops" value={summary.distinct_outlets.toLocaleString('en-PK')} />
          <DashboardMetric label="Days complete" value={`${summary.days_complete} / ${summary.planned_working_days}`} note={summary.planned_working_days ? 'configured working days' : 'not configured'} />
          <DashboardMetric label="Visit quota" value={summary.visit_quota ? summary.visit_quota.toLocaleString('en-PK') : 'Not set'} />
        </section>
        {data.is_embrace_compatible ? <section className="survey-dashboard-metrics survey-legacy-metrics" aria-label="Embrace availability summary">
          <DashboardMetric label="Premium & Comfort" value={summary.premium_comfort_presence} note="shops with a reported range" />
          <DashboardMetric label="Savers" value={summary.savers_presence} note="shops with a reported range" />
          <DashboardMetric label="Known competitor category" value={summary.known_competitor_category_presence} />
          <DashboardMetric label="Premium availability index" value={percentage(summary.premium_comfort_availability_index)} />
          <DashboardMetric label="Savers availability index" value={percentage(summary.savers_availability_index)} />
        </section> : null}
        <section className="survey-dashboard-grid">
          <article className="content-card survey-chart-card"><div><h3>Coverage by city</h3><p>Submitted survey visits in the selected reporting scope.</p></div><div className="survey-chart"><ResponsiveContainer height="100%" width="100%"><BarChart accessibilityLayer data={data.cities} margin={{ top: 14, right: 12, left: -20, bottom: 4 }}><CartesianGrid stroke="var(--line)" strokeDasharray="3 5" vertical={false} /><XAxis dataKey="city" tick={{ fill: 'var(--muted)', fontSize: 12 }} /><YAxis allowDecimals={false} tick={{ fill: 'var(--muted)', fontSize: 12 }} /><Tooltip contentStyle={chartTooltipStyle} cursor={{ fill: 'var(--surface-soft)' }} /><Bar dataKey="visits" fill={chartColors.coverage_by_city} name="Survey visits" radius={[5, 5, 0, 0]} /></BarChart></ResponsiveContainer></div></article>
          {data.is_embrace_compatible ? <article className="content-card survey-chart-card"><div><h3>Availability by city</h3><p>Reported range and competitor presence, not an inferred availability rate.</p></div><div className="survey-chart"><ResponsiveContainer height="100%" width="100%"><BarChart accessibilityLayer data={data.cities} margin={{ top: 14, right: 12, left: -20, bottom: 4 }}><CartesianGrid stroke="var(--line)" strokeDasharray="3 5" vertical={false} /><XAxis dataKey="city" tick={{ fill: 'var(--muted)', fontSize: 12 }} /><YAxis allowDecimals={false} tick={{ fill: 'var(--muted)', fontSize: 12 }} /><Tooltip contentStyle={chartTooltipStyle} cursor={{ fill: 'var(--surface-soft)' }} /><Legend wrapperStyle={{ fontSize: 12 }} /><Bar dataKey="premium_comfort_presence" fill={chartColors.availability_by_city.premium_comfort_presence} name="Premium & Comfort" radius={[4, 4, 0, 0]} /><Bar dataKey="savers_presence" fill={chartColors.availability_by_city.savers_presence} name="Savers" radius={[4, 4, 0, 0]} /><Bar dataKey="known_competitor_category_presence" fill={chartColors.availability_by_city.known_competitor_category_presence} name="Known competitors" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div></article> : null}
          {availabilitySections.length ? <article className="content-card survey-chart-card survey-availability-pie"><div className="survey-question-chart-heading"><div><h3>Availability mix</h3><p>One visit may contribute to multiple sections; values are observation counts, not market share.</p></div><label>City<select aria-label="Availability chart city" onChange={(event) => setAvailabilityCity(event.target.value)} value={selectedAvailabilityCity}><option value="">All cities</option>{availabilityCities.map((item) => <option key={item.city} value={item.city}>{item.city}</option>)}</select></label></div><div className="survey-chart">{availabilityTotal ? <SurveyDistributionChart color={chartColors.coverage_by_city} colors={chartColors.availability_sections} data={availabilitySections} showLegend={false} type={availabilityChartType} /> : <p className="survey-chart-empty">No availability observations for this city.</p>}</div><ul aria-label="Availability sections" className="survey-pie-legend">{availabilitySections.map((section, index) => { const sectionColor = availabilitySectionColor(section.key, index); return <li key={section.key}><span style={{ backgroundColor: sectionColor }} /><strong style={{ color: sectionColor }}>{section.label}</strong><b>{section.count.toLocaleString('en-PK')}</b></li>; })}</ul></article> : null}
          {data.question_summaries.length ? <article className="content-card survey-chart-card"><div className="survey-question-chart-heading"><div><h3>Question distribution</h3><p>Aggregate answers only; sensitive shop-owner information remains restricted.</p></div><label>Question<select aria-label="Survey dashboard question" onChange={(event) => onQuestionChange(event.target.value)} value={selectedQuestion?.id ?? ''}>{data.question_summaries.map((question) => <option key={question.id} value={question.id}>{question.label}</option>)}</select></label></div>{selectedQuestion ? <div className="survey-chart"><SurveyDistributionChart color={chartColors.question_default} colors={chartColors.question_options} data={selectedQuestion.options} type={questionChartType} /></div> : null}</article> : null}
        </section>
        {reportingGroups.length ? <section className="content-card survey-grouped-report"><div><h3>Grouped survey results</h3><p>Leaf answers are grouped from stable questionnaire keys and follow the reporting filters above; the mobile form remains unchanged.</p></div><div className="survey-report-group-list">{reportingGroups.map((group) => <article className="survey-report-group" key={group.key}><h4>{group.label}</h4><div className="survey-report-column-grid">{group.columns.map((column) => <section className="survey-report-column" key={column.key}><h5>{column.label}</h5>{column.questions.map((question) => <div className="survey-report-question" key={question.key}><span>{question.label}</span><ul>{question.options.map((option) => <li key={option.value}><small>{option.label}</small><strong>{option.count.toLocaleString('en-PK')}</strong></li>)}</ul></div>)}</section>)}</div></article>)}</div></section> : null}
      </> : null}
    </DataPanel>
  </>;
}

function Pagination({ page, previous, next, onChange }: { page: number; previous: string | null | undefined; next: string | null | undefined; onChange: (page: number) => void }) {
  return <div className="attendance-pagination"><span>Page {page}</span><div><button className="button secondary" disabled={!previous} onClick={() => onChange(Math.max(1, page - 1))} type="button">Previous</button><button className="button secondary" disabled={!next} onClick={() => onChange(page + 1)} type="button">Next</button></div></div>;
}
